import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  GROWTH_AREAS, 
  DEVELOPMENT_TOPICS, 
  bookOrder as BIBLE_BOOKS 
} from '../utils/constants';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Add enrichment filter keys
const AGE_RELEVANCE_KEYS = [
  'elementary', 'middleSchool', 'highSchool', 'youngAdult', 'middleAge', 'wisdomYears'
];
const RELATIONSHIP_RELEVANCE_KEYS = [
  'youth', 'singleYoungAdult', 'singleAdult', 'dating', 'engaged', 'earlyMarriage', 'experiencedMarriage', 'marriedWithYoungKids', 'marriedWithOlderKids', 'grandparents', 'separated', 'divorced', 'widowed'
];
const DISCIPLESHIP_STAGE_KEYS = [
  'notCurrentlyBeliever', 'newBeliever', 'growingDisciple', 'discipleMaker', 'sageStage'
];
const CHRISTIAN_EXPERIENCE_KEYS = [
  'noExperienceYet', 'newToFaith', 'grewUpStillWalking', 'cameToFaithNowWalking', 'crisisLeaningIn', 'crisisLeaningOut', 'walkedAwayCurrently', 'walkedAwayNowOpen', 'walkedAwayNowReturned'
];

const SearchVerses = ({ onVerseSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrowthArea, setSelectedGrowthArea] = useState('');
  const [selectedDevTopic, setSelectedDevTopic] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBookListOpen, setIsBookListOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [versesPerPage] = useState(7);
  // Add filter state
  const [selectedAge, setSelectedAge] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState('');
  const [selectedDiscipleship, setSelectedDiscipleship] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');

  const searchVerses = async () => {
    setLoading(true);
    try {
      const versesRef = collection(db, 'verses');
      let q = query(versesRef);

      // Get all verses first
      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Then filter client-side for more flexible matching
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        results = results.filter(verse => {
          return (
            verse.text?.toLowerCase().includes(searchLower) ||
            verse.reference?.toLowerCase().includes(searchLower) ||
            verse.tags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
            verse.categories?.some(cat => cat.toLowerCase().includes(searchLower)) ||
            verse.growthArea?.toLowerCase().includes(searchLower) ||
            verse.devTopic?.toLowerCase().includes(searchLower)
          );
        });
      }

      // Apply filters
      if (selectedBook) {
        results = results.filter(verse => {
          const verseBook = verse.reference?.split(' ')[0];
          let bookName = verseBook;
          if (['1', '2', '3'].includes(bookName)) {
            bookName = verse.reference?.split(' ').slice(0, 2).join(' ');
          }
          return bookName === selectedBook;
        });
      }

      if (selectedGrowthArea) {
        results = results.filter(verse => 
          verse.growthArea === selectedGrowthArea
        );
      }

      if (selectedDevTopic) {
        results = results.filter(verse => 
          verse.devTopic === selectedDevTopic
        );
      }

      if (selectedAge) {
        results = results.filter(verse => verse.ageRelevance && verse.ageRelevance[selectedAge] >= 0.5);
      }
      if (selectedRelationship) {
        results = results.filter(verse => verse.relationshipRelevance && verse.relationshipRelevance[selectedRelationship] >= 0.5);
      }
      if (selectedDiscipleship) {
        results = results.filter(verse => verse.discipleshipStageRelevance && verse.discipleshipStageRelevance[selectedDiscipleship] >= 0.5);
      }
      if (selectedExperience) {
        results = results.filter(verse => verse.christianExperienceRelevance && verse.christianExperienceRelevance[selectedExperience] >= 0.5);
      }

      console.log('Search results:', {
        searchTerm,
        selectedGrowthArea,
        selectedDevTopic,
        selectedBook,
        resultsCount: results.length,
        results
      });

      setVerses(results);
    } catch (error) {
      console.error('Error searching verses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchVerses();
  }, [searchTerm, selectedGrowthArea, selectedDevTopic, selectedBook]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchVerses();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Helper to clear a specific filter
  const clearFilter = (filterType) => {
    switch(filterType) {
      case 'book':
        setSelectedBook('');
        break;
      case 'growthArea':
        setSelectedGrowthArea('');
        break;
      case 'devTopic':
        setSelectedDevTopic('');
        break;
      default:
        break;
    }
  };

  // Add these pagination helper functions
  const indexOfLastVerse = currentPage * versesPerPage;
  const indexOfFirstVerse = indexOfLastVerse - versesPerPage;
  const currentVerses = verses.slice(indexOfFirstVerse, indexOfLastVerse);
  const totalPages = Math.ceil(verses.length / versesPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Add this to reset pagination when search/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGrowthArea, selectedDevTopic, selectedBook]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Search Verses</h1>
      
      {/* Active Filters Breadcrumbs */}
      {(selectedBook || selectedGrowthArea || selectedDevTopic) && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-sm text-gray-600">Filters:</span>
          {selectedBook && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              {selectedBook}
              <button
                onClick={() => clearFilter('book')}
                className="ml-2 hover:text-blue-600"
              >
                ×
              </button>
            </span>
          )}
          {selectedGrowthArea && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              {selectedGrowthArea}
              <button
                onClick={() => clearFilter('growthArea')}
                className="ml-2 hover:text-green-600"
              >
                ×
              </button>
            </span>
          )}
          {selectedDevTopic && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
              {selectedDevTopic}
              <button
                onClick={() => clearFilter('devTopic')}
                className="ml-2 hover:text-gray-600"
              >
                ×
              </button>
            </span>
          )}
          {(selectedBook || selectedGrowthArea || selectedDevTopic) && (
            <button
              onClick={() => {
                setSelectedBook('');
                setSelectedGrowthArea('');
                setSelectedDevTopic('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search verses..."
          className="w-full p-2 border rounded"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select
            value={selectedGrowthArea}
            onChange={(e) => setSelectedGrowthArea(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Growth Areas</option>
            {GROWTH_AREAS.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>

          <select
            value={selectedDevTopic}
            onChange={(e) => setSelectedDevTopic(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Development Topics</option>
            {DEVELOPMENT_TOPICS.map(topic => (
              <option key={topic} value={topic}>{topic}</option>
            ))}
          </select>

          {/* New enrichment filters */}
          <select
            value={selectedAge}
            onChange={e => setSelectedAge(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Age Groups</option>
            {AGE_RELEVANCE_KEYS.map(key => (
              <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
            ))}
          </select>
          <select
            value={selectedRelationship}
            onChange={e => setSelectedRelationship(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Relationship Statuses</option>
            {RELATIONSHIP_RELEVANCE_KEYS.map(key => (
              <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
            ))}
          </select>
          <select
            value={selectedDiscipleship}
            onChange={e => setSelectedDiscipleship(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Discipleship Stages</option>
            {DISCIPLESHIP_STAGE_KEYS.map(key => (
              <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
            ))}
          </select>
          <select
            value={selectedExperience}
            onChange={e => setSelectedExperience(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Christian Experiences</option>
            {CHRISTIAN_EXPERIENCE_KEYS.map(key => (
              <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
            ))}
          </select>
        </div>

        {/* Bible Books Accordion */}
        <div>
          <button 
            onClick={() => setIsBookListOpen(!isBookListOpen)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Search by Book of the Bible
          </button>
          
          {isBookListOpen && (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 p-4 bg-gray-50 rounded-lg">
              {BIBLE_BOOKS.map(book => (
                <button
                  key={book}
                  onClick={() => {
                    setSelectedBook(book);
                    setIsBookListOpen(false);
                    searchVerses();
                  }}
                  className={`text-left text-sm p-1 hover:text-blue-600 ${
                    selectedBook === book ? 'text-blue-600 font-medium' : 'text-gray-600'
                  }`}
                >
                  {book}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-600">
          Found {verses.length} verses
          {verses.length > 0 && ` (Showing ${indexOfFirstVerse + 1}-${Math.min(indexOfLastVerse, verses.length)})`}
        </div>

        {/* Results List */}
        {loading ? (
          <div className="text-center">Loading...</div>
        ) : (
          <>
            <div className="space-y-4">
              {currentVerses.map(verse => (
                <div key={verse.id} className="border rounded-lg p-4">
                  <h3 className="font-medium">{verse.reference}</h3>
                  <p className="text-gray-600 mt-1">{verse.text}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {verse.growthArea && (
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        {verse.growthArea}
                      </span>
                    )}
                    {verse.devTopic && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {verse.devTopic}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => onVerseSelect(verse)}
                      className="px-3 py-1.5 bg-[#0076BE] text-white text-sm rounded-md hover:bg-[#005991] transition-colors duration-200"
                    >
                      Create Your Message
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {verses.length > versesPerPage && (
              <div className="mt-6 flex items-center justify-center space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-full ${
                    currentPage === 1 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber)}
                          className={`px-3 py-1 rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber}>...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-full ${
                    currentPage === totalPages 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchVerses; 